/* global GM_STATE */
import { signal } from '@preact/signals'
import SearchSpringService from 'libs/searchspring/searchspring-service'
import debounce from 'lodash.debounce'

const { searchSpringConfig } = GM_STATE.integrations

const selectors = {
  COLLECTION_IGNORES: ['All'],
  TRENDING_SESSION_STORAGE: 'searchspring-trending'
}

function appState() {
  const SearchSpringInstance = new SearchSpringService({
    siteId: searchSpringConfig.siteId,
    enable: searchSpringConfig.enable
  })

  const originalQuery = signal('')
  const correctedQuery = signal('')
  const searchValue = signal('')
  const suggestions = signal([])
  const isOpenDropdown = signal(false)
  const isLoading = signal(false)
  const results = signal([])
  const collections = signal([])
  const redirectUrl = signal('')
  const totalResults = signal(0)
  const searchTrendingValue = signal([])
  const isTrendingActive = signal(false)

  const getSuggestion = debounce(async (value) => {
    isOpenDropdown.value = true
    isTrendingActive.value = false
    setIsLoading(true)
    setOriginalQuery(value)

    const response = await SearchSpringInstance.getSuggestion(value)
    const data = await response.json()

    suggestions.value = getSuggestionFiltered(data)
    setCorrectedQuery(data?.query || '')

    const suggestedQuery =
      suggestions.value && suggestions.value.length && suggestions.value[0] && suggestions.value[0].text ? suggestions.value[0].text : originalQuery.value
    const finalQuery = correctedQuery.value || suggestedQuery

    setSearchValue(finalQuery)
    await onSearching(finalQuery)
    setTimeout(() => {
      setIsLoading(false)
    }, 100)
  }, 500)

  const getTrending = async () => {
    const trendingSession = sessionStorage.getItem(selectors.TRENDING_SESSION_STORAGE)
    const trendingSessionData = JSON.parse(trendingSession)

    if (!trendingSessionData) {
      setIsLoading(true)

      const response = await SearchSpringInstance.getTrending()

      if (response) {
        setSearchTrendingValue(response?.trending?.queries)
      }
      setTimeout(() => {
        setIsLoading(false)
        sessionStorage.setItem(selectors.TRENDING_SESSION_STORAGE, JSON.stringify(searchTrendingValue.value))
      }, 200)
    } else {
      setSearchTrendingValue(trendingSessionData)
    }
  }

  async function onSearching(query) {
    if (query) {
      await onFetch(query)
    } else {
      closeDropdown()
    }
  }

  async function onFetch(query) {
    try {
      const payload = {
        perPage: 12,
        selectedValues: {
        }
      }

      const response = await SearchSpringInstance.getSearchResults(query, 'autocomplete', payload)

      if (response) {
        results.value = response.results
        collections.value = getCollections(response.facets, 'collection_name')

        if (response.merchandising && response.merchandising.redirect !== null) {
          redirectUrl.value = response.merchandising.redirect
        }

        if (response.pagination) {
          totalResults.value = response.pagination.totalResults
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Could not fetch from searchspring for query: ', query, e)
    }
  }

  function getCollections(facets, target) {
    const relevantFacet = facets.find((item) => item.field === target)

    if (relevantFacet && relevantFacet.values) {
      return relevantFacet.values.filter((x) => !selectors.COLLECTION_IGNORES.includes(x.value)).slice(0, 5)
    }

    return []
  }

  function closeDropdown() {
    isOpenDropdown.value = false
    results.values = []
    totalResults.value = 0
    collections.value = []
  }

  function getSuggestionFiltered(suggestions) {
    const values = []

    if (suggestions.suggested) {
      values.push(suggestions.suggested)
    }

    if (suggestions.alternatives && suggestions.alternatives.length) {
      suggestions.alternatives.forEach((item) => {
        if (!item.text.includes('?')) {
          values.push(item)
        }
      })
    }

    return values
  }

  function setOriginalQuery(value) {
    originalQuery.value = value
  }

  function setIsLoading(value) {
    isLoading.value = value
  }

  function setSearchValue(value) {
    searchValue.value = value
  }

  function setIsTrendingActive(value) {
    isTrendingActive.value = value
  }

  function setSearchTrendingValue(value) {
    searchTrendingValue.value = value
  }

  function setCorrectedQuery(value) {
    correctedQuery.value = value
  }

  function setIsOpenDropdown(value) {
    isOpenDropdown.value = value
  }

  return {
    originalQuery,
    correctedQuery,
    searchValue,
    searchTrendingValue,
    suggestions,
    collections,
    results,
    isOpenDropdown,
    isLoading,
    totalResults,
    redirectUrl,
    getSuggestion,
    isTrendingActive,
    setIsOpenDropdown,
    getTrending,
    setIsTrendingActive,
    setOriginalQuery,
    closeDropdown,
    setSearchValue,
    setCorrectedQuery
  }
}

export default appState
